import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Veteran Passage';
const SITE_URL = 'https://veteranpassage.org';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

const seoConfig = {
  '/': {
    title: 'Veteran Passage | Navigate Benefits, Careers & Civilian Pathways',
    description: 'Find your personalized Veteran Passage. Navigate VA benefits, second-chance jobs, and legal discharge upgrades based on your DD-214. Serving veterans of all discharges.',
    keywords: 'veteran benefits, OTH discharge benefits, veteran small business support, military transition, DD-214 decoder, discharge upgrade, second chance jobs for veterans'
  },
  '/login': {
    title: 'Sign In | Veteran Passage',
    description: 'Sign in to your Veteran Passage account to access personalized benefits, resources, and career pathways based on your service history.',
    keywords: 'veteran login, veteran passage sign in, veteran benefits portal'
  },
  '/signup': {
    title: 'Create Account | Veteran Passage',
    description: 'Join Veteran Passage to get personalized benefit recommendations, career pathways, and discharge upgrade resources. All discharge types welcome.',
    keywords: 'veteran sign up, veteran registration, OTH veteran support, military veteran services'
  },
  '/dashboard': {
    title: 'Dashboard | Veteran Passage',
    description: 'Your personalized veteran dashboard. Track saved resources, access benefits navigator, and manage your transition pathway.',
    keywords: 'veteran dashboard, veteran benefits tracker, military transition tools'
  },
  '/navigator': {
    title: 'Benefits Navigator | Veteran Passage',
    description: 'Answer a few questions to discover VA benefits, grants, and programs you qualify for — even with an OTH or less-than-honorable discharge.',
    keywords: 'can i get VA benefits with OTH discharge, veteran benefits navigator, discharge upgrade eligibility, VA benefits checker'
  },
  '/resources': {
    title: 'Resource Directory | Veteran Passage',
    description: 'Curated directory of second-chance jobs, pro-bono legal aid, OTH-friendly grants, and veteran support services. Verified organizations nationwide.',
    keywords: 'veteran resources, second chance jobs for veterans, veteran legal aid, OTH friendly grants, veteran small business loans, military veterans support services'
  },
  '/profile': {
    title: 'My Profile | Veteran Passage',
    description: 'Update your service history and discharge information to get better personalized benefit recommendations.',
    keywords: 'veteran profile, veteran account settings'
  }
};

export function PageSEO({ path }) {
  const config = seoConfig[path] || seoConfig['/'];

  return (
    <Helmet>
      <title>{config.title}</title>
      <meta name="description" content={config.description} />
      <meta name="keywords" content={config.keywords} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={config.title} />
      <meta property="og:description" content={config.description} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:url" content={`${SITE_URL}${path}`} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={config.title} />
      <meta name="twitter:description" content={config.description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {/* Canonical */}
      <link rel="canonical" href={`${SITE_URL}${path}`} />
    </Helmet>
  );
}

export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Veteran Passage',
    alternateName: 'VeteranPassage',
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description: 'Empowering veterans of all discharges to navigate benefits, careers, and civilian life with dignity.',
    foundingDate: '2026',
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@veteranpassage.org',
      contactType: 'customer support'
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    },
    serviceType: [
      'Veteran Benefits Navigation',
      'Discharge Upgrade Resources',
      'Veteran Career Services',
      'Military Transition Support'
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
